import {
  GoogleAuthError,
  SheetProjectInfo,
  sheetsApi,
  SheetType,
} from '../services/api';
import { logger } from '../services/logger';
import { notify } from '../ui';

export class SheetsImportCtrl {
  declare projectId: string;
  declare importTypeName: string;
  declare importType: SheetType;
  /**
   * Whether we should show date input on first step
   */
  dateRequired = false;
  tableId: string | null = null;
  loaded = false;
  btnLoadingOn = false;
  showErrorMsg = false;
  newTableDate = '';
  activeTable: SheetProjectInfo['exports'][number] | null = null;
  projectInfo: SheetProjectInfo = {
    name: '',
    exports: [],
    taken_dates: [],
  };

  static $inject = ['$state', '$filter', '$window', '$scope', '$translate'];

  constructor(
    private $state: {
      params: { id: string; type: SheetType; tableId?: string };
      go(route: string, params?: { [key: string]: any }): void;
    },
    private $filter: angular.IFilterService,
    private $window: angular.IWindowService,
    private $scope: angular.IScope,
    private $translate: angular.translate.ITranslateService,
  ) {
    this.importType = $state.params.type;
    // human-readable id for template
    this.importTypeName =
      { [sheetsApi.SHEET_TYPE.UNITS_DATA]: 'unitsData' }[$state.params.type] ||
      'prices';
    this.projectId = $state.params.id;
    this.tableId = $state.params.tableId || null;
    this.dateRequired = $state.params.type === sheetsApi.SHEET_TYPE.PRICES;

    $scope.$on('$stateChangeSuccess', (event, toState, toParams, fromState) => {
      if (
        toState.name === 'sheets.import' &&
        fromState.name === 'sheets.import.step2'
      ) {
        this.activeTable = null;
        this.tableId = null;
      }
    });

    this.loadData();
  }

  async loadData() {
    try {
      const resp = await sheetsApi.getProjectInfo({
        type: this.importType,
        projectId: this.projectId,
      });

      this.projectInfo = resp;
      this.loaded = true;

      if (this.projectInfo.exports.length) {
        if (this.tableId) {
          this.openStepTwo(this.tableId);
        } else if (this.importType === sheetsApi.SHEET_TYPE.UNITS_DATA) {
          // when we working with unit data export — it's allowed to have only one draft export
          // so we are permanently redirecting to second step
          const [table] = this.projectInfo.exports;

          this.openStepTwo(table.id);
        }
      } else {
        this.$state.go('sheets.import', {
          type: this.importType,
          projectId: this.projectId,
        });
      }

      this.$scope.$apply();
    } catch (error) {
      if (error instanceof GoogleAuthError) {
        return this.authRedirect(error.getUrl());
      }

      notify.error('Error loading sheet data', error);
    }
  }

  closeSidepanel() {
    this.$state.go('sheets');
  }

  openStepTwo(tableId: string) {
    this.tableId = tableId;
    [this.activeTable = null] = this.$filter('filter')(
      this.projectInfo.exports,
      { id: tableId },
      true,
    );

    this.$state.go('sheets.import.step2', { tableId });
  }

  openStepTwoAndSheet(tableId: string, tableUrl: string) {
    this.openStepTwo(tableId);

    this.openTable(tableUrl);
  }

  async createTable(date?: string) {
    this.btnLoadingOn = true;

    try {
      const resp = await sheetsApi.createTable({
        date,
        projectId: this.projectId,
        type: this.importType,
      });

      // in some cases (units data) there can be missing name field
      // so we will merge objects to keep as much data as possible
      this.projectInfo = { ...this.projectInfo, ...resp };

      const sheetData =
        this.projectInfo.exports[this.projectInfo.exports.length - 1];

      this.openStepTwoAndSheet(sheetData.id, sheetData.url);
    } catch (error) {
      if (error instanceof GoogleAuthError) {
        return this.authRedirect(error.getUrl());
      }

      notify.error('Error creating table', error);
    } finally {
      this.btnLoadingOn = false;
      this.$scope.$apply();
    }
  }

  async importTable(tableId: string) {
    this.btnLoadingOn = true;
    this.showErrorMsg = false;

    try {
      const resp = await (this.importType === sheetsApi.SHEET_TYPE.UNITS_DATA
        ? sheetsApi.importUnitsDataTable(tableId)
        : sheetsApi.importTable(tableId));

      this.closeSidepanel();

      notify.success(
        [
          this.$translate.instant(
            'projects.page.sheets.step2.import.btn.toast',
          ),
          resp.message,
        ]
          .filter(Boolean)
          .join(' '),
      );
    } catch (error) {
      if (error instanceof GoogleAuthError) {
        return this.authRedirect(error.getUrl());
      }

      if (error.error) {
        this.showErrorMsg = true;

        logger.error('Error importing table', {
          error,
        });
      }
    } finally {
      this.btnLoadingOn = false;
      this.$scope.$apply();
    }
  }

  async deleteTable(tableId: string) {
    try {
      await sheetsApi.deleteTable(tableId);

      this.closeSidepanel();

      notify.success(
        this.$translate.instant('projects.page.sheets.step2.cancel.btn.toast'),
      );
    } catch (error) {
      notify.error('Error deleting table', error);
    }
  }

  openTable(url: string) {
    this.$window.open(url, '_blank');
  }

  authRedirect(authUrl: string) {
    const redirectUrl = `objects%23/sheets/import/${this.importType}/${this.projectId}`;

    this.$window.location.href = `${this.$window.location.origin + authUrl}/?redirect=${redirectUrl}`;
  }
}
